import React from 'react';
import { Box, Link as MuiLink, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { useLocalizedPath } from '../../shared/hooks/useLocalizedPath';
import { SEO } from './SEO';
import { Section, PageHeader } from './PageSection';
import { LegalBlock, LegalHeading, LegalList, LegalNote } from './LegalSectionBody';

const contentUseCase = new ContentUseCase();

export const PrivacyPage: React.FC = () => {
  const { language } = useLanguage();
  const lp = useLocalizedPath();
  const { privacy } = contentUseCase.getLegalContent(language);

  return (
    <Box>
      <SEO page="privacy" />

      <PageHeader title={privacy.title} subtitle={privacy.intro.description} />

      <Section maxWidth="md">
        <LegalBlock>
          <LegalHeading>{privacy.responsible.title}</LegalHeading>
          <Typography variant="body1" paragraph>
            {privacy.responsible.description}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {privacy.responsible.name}
          </Typography>
          <Typography variant="body1">{privacy.responsible.address}</Typography>
          <Typography variant="body1">{privacy.responsible.city}</Typography>
          <Typography variant="body1">{privacy.responsible.country}</Typography>
          <Typography variant="body1" sx={{ mt: 1.5 }}>
            {privacy.responsible.email}
          </Typography>
        </LegalBlock>

        <LegalBlock>
          <LegalHeading>{privacy.hosting.title}</LegalHeading>
          <Typography variant="body1" paragraph>
            {privacy.hosting.description}
          </Typography>
          <LegalList items={privacy.hosting.data} />
          <LegalNote>{privacy.hosting.legal}</LegalNote>
          <LegalNote>{privacy.hosting.retention}</LegalNote>
          <LegalNote>{privacy.hosting.transfer}</LegalNote>
        </LegalBlock>

        <LegalBlock>
          <LegalHeading>{privacy.cookies.title}</LegalHeading>
          <Typography variant="body1" paragraph>
            {privacy.cookies.description}
          </Typography>
          <LegalNote>{privacy.cookies.legal}</LegalNote>
          <Typography variant="body1" sx={{ mt: 2 }}>
            {privacy.cookies.withdraw}{' '}
            <MuiLink component={Link} to={lp('/cookie-settings')} sx={{ color: 'primary.dark' }}>
              {language === 'de' ? 'Cookie-Einstellungen' : 'Cookie settings'}
            </MuiLink>
          </Typography>
        </LegalBlock>

        <LegalBlock>
          <LegalHeading>{privacy.analytics.title}</LegalHeading>
          <Typography variant="body1" paragraph>
            {privacy.analytics.description}
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontWeight: 600 }}>
            {privacy.analytics.loadNote}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
            {privacy.analytics.dataTitle}
          </Typography>
          <LegalList items={privacy.analytics.data} />
          <Typography variant="body1" paragraph>
            {privacy.analytics.noAds}
          </Typography>
          <LegalNote>{privacy.analytics.legal}</LegalNote>
          <LegalNote>{privacy.analytics.retention}</LegalNote>
          <LegalNote>{privacy.analytics.transfer}</LegalNote>
          <Typography variant="body1" sx={{ mt: 2 }}>
            <MuiLink component={Link} to={lp('/cookie-settings')} sx={{ color: 'primary.dark' }}>
              {language === 'de'
                ? 'Zustimmung hier jederzeit ändern'
                : 'Change your consent at any time here'}
            </MuiLink>
          </Typography>
        </LegalBlock>

        <LegalBlock>
          <LegalHeading>{privacy.contactForm.title}</LegalHeading>
          <Typography variant="body1" paragraph>
            {privacy.contactForm.description}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
            {privacy.contactForm.dataTitle}
          </Typography>
          <LegalList items={privacy.contactForm.data} />
          <Typography variant="body1" paragraph>
            {privacy.contactForm.healthNote}
          </Typography>
          <LegalNote>{privacy.contactForm.legal}</LegalNote>
          <LegalNote>{privacy.contactForm.retention}</LegalNote>
          <LegalNote>{privacy.contactForm.processor}</LegalNote>
        </LegalBlock>

        <LegalBlock>
          <LegalHeading>{privacy.instagram.title}</LegalHeading>
          <Typography variant="body1" paragraph>
            {privacy.instagram.description}
          </Typography>
          <Typography variant="body1" paragraph>
            {privacy.instagram.images}
          </Typography>
          <LegalNote>{privacy.instagram.legal}</LegalNote>
          <LegalNote>{privacy.instagram.controller}</LegalNote>
        </LegalBlock>

        <LegalBlock>
          <LegalHeading>{privacy.external.title}</LegalHeading>
          <Typography variant="body1">{privacy.external.description}</Typography>
        </LegalBlock>

        <LegalBlock>
          <LegalHeading>{privacy.rights.title}</LegalHeading>
          <Typography variant="body1" paragraph>
            {privacy.rights.description}
          </Typography>
          <LegalList items={privacy.rights.list} />
          <Typography variant="body1" paragraph>
            {privacy.rights.contact}
          </Typography>
          <Typography variant="body1">{privacy.rights.complaint}</Typography>
        </LegalBlock>

        <LegalBlock last>
          <LegalHeading>{privacy.changes.title}</LegalHeading>
          <Typography variant="body1">{privacy.changes.description}</Typography>
        </LegalBlock>
      </Section>
    </Box>
  );
};
